import * as React from "react";
import * as __intl__ from "@frameio/i18n";
export type Text = {
    actionName: string;
    subtitle: string;
    focusSubject: string;
    focusSubjectPlaceholder: string;
    focusSubjectInfoLabel: string;
    focusSubjectInvalid: string;
    focusSubjectTooManyKeywords: ({ max }: {
        max: number;
    }) => string;
    focusSubjectKeywordTooLong: ({ max }: {
        max: number;
    }) => string;
    aspectRatio: string;
    aspectRatioPlaceholder: string;
    infoText: string;
    learnMore: string;
    generate: string;
    cancel: string;
    dialogCloseLabel: string;
    overDurationLabel: string;
    invalidCodecLabel: string;
    validationWarningSummary: ({ failedCount, totalCount }: {
        failedCount: number;
        totalCount: number;
    }) => string;
    validationWarningIntro: string;
    validationWarningContinue: string;
    includeOtioFile: string;
    includeOtioFileTooltip: string;
    processingSummary: ({ processingCount, totalCount }: {
        processingCount: number;
        totalCount: number;
    }) => string;
    processingIntro: string;
    processingRowStatus: string;
    processingSpinnerLabel: string;
};
export type Deps = {};
export default {
    DEFINE(_messages: Text, {}: Deps = {}): void {
        return Object.defineProperties(this, {
            /** Name of the reframe video action. */
            actionName: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.actionName?c=93smr95fd1tr", defaultMessage: "Reframe Video" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Subtitle of the reframe video dialog. */
            subtitle: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.subtitle?c=s9ku9dvhzh8q", defaultMessage: "Resize your video to fit different aspect ratios." }));
                },
                enumerable: true,
                configurable: true
            },
            /** This label is used to describe which subject in the video should be focused on. */
            focusSubject: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.focusSubject?c=22e2clbf0lhjp", defaultMessage: "Focus subject (Optional)" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Focus subject placeholder. */
            focusSubjectPlaceholder: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.focusSubjectPlaceholder?c=22hljuzzg2cz", defaultMessage: "e.g. person, product or logo" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Accessible label and tooltip for the focus subject info icon linking to docs. */
            focusSubjectInfoLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.focusSubjectInfoLabel?c=168ekac0qhk8r", defaultMessage: "Learn more about focus subjects" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Error shown when the focus subject contains disallowed characters. */
            focusSubjectInvalid: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.focusSubjectInvalid?c=1t0acl5g1uk1z", defaultMessage: "Only letters, numbers, spaces, hyphens, underscores, and commas are allowed." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Error shown when the focus subject has more comma-separated keywords than allowed. */
            focusSubjectTooManyKeywords: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ max }: {
                        max: number;
                    }): string => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.focusSubjectTooManyKeywords?c=3ov6c1irwdet6", defaultMessage: "You can enter up to {max} keywords, separated by commas." }, { max: max }));
                },
                enumerable: true,
                configurable: true
            },
            /** Error shown when a single comma-separated keyword is too long. */
            focusSubjectKeywordTooLong: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ max }: {
                        max: number;
                    }): string => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.focusSubjectKeywordTooLong?c=1gpesgi7i8iac", defaultMessage: "Each keyword must be {max} characters or fewer." }, { max: max }));
                },
                enumerable: true,
                configurable: true
            },
            /** Aspect ratio label. */
            aspectRatio: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.aspectRatio?c=30dpw5mapdcja", defaultMessage: "Aspect Ratio" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Aspect ratio placeholder. */
            aspectRatioPlaceholder: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.aspectRatioPlaceholder?c=1r1x0klx2pr0q", defaultMessage: "Select aspect ratios" }));
                },
                enumerable: true,
                configurable: true
            },
            /** This text is used to describe the limitations of the reframe video feature. */
            infoText: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.infoText?c=1r79jmunieuqf", defaultMessage: "This feature only supports H.264 and H.265 video files up to 30 minutes long." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for the link to documentation about the reframe video feature. */
            learnMore: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.learnMore?c=2bu5tw74q9bww", defaultMessage: "Learn more" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Generate button text. */
            generate: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.generate?c=3ro1smuto4dg5", defaultMessage: "Generate Variations" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Cancel button text. */
            cancel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.cancel?c=2ojhd8rzicmo0", defaultMessage: "Cancel" }));
                },
                enumerable: true,
                configurable: true
            },
            /** This text is used to close the dialog. */
            dialogCloseLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.dialogCloseLabel?c=2g2kiprif3vje", defaultMessage: "Close Dialog" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for videos that exceed the maximum duration */
            overDurationLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.overDurationLabel?c=2svjm5t6gwpoa", defaultMessage: "Over 30 minutes" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for videos with unsupported codec */
            invalidCodecLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.invalidCodecLabel?c=qiaf1uatf7fc", defaultMessage: "Unsupported codec" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Summary text for the validation warning accordion header */
            validationWarningSummary: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ failedCount, totalCount }: {
                        failedCount: number;
                        totalCount: number;
                    }): string => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.validationWarningSummary?c=1dzqx2ev854wu", defaultMessage: "{failedCount} of {totalCount} videos won''t be reframed" }, { failedCount: failedCount, totalCount: totalCount }));
                },
                enumerable: true,
                configurable: true
            },
            /** Introduction text for validation warning in the expanded accordion */
            validationWarningIntro: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.validationWarningIntro?c=22elnnywx59qq", defaultMessage: "The following videos will not be included in reframe:" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Continuation text for validation warning when some videos are valid */
            validationWarningContinue: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.validationWarningContinue?c=2mmjna9kcny51", defaultMessage: "You can still generate variations for the remaining videos." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Label for OTIO file toggle */
            includeOtioFile: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.includeOtioFile?c=3p9lf2bxe5doe", defaultMessage: "Include OTIO file" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Tooltip explaining OTIO file option */
            includeOtioFileTooltip: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.includeOtioFileTooltip?c=zwuqfy7bs9nm", defaultMessage: "Creates an editable OTIO file for adjusting reframing in supported editing apps." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Summary text shown in the processing accordion header. */
            processingSummary: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => ({ processingCount, totalCount }: {
                        processingCount: number;
                        totalCount: number;
                    }): string => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.processingSummary?c=2xzhaouqqgt5g", defaultMessage: "{processingCount} of {totalCount} videos are still processing" }, { processingCount: processingCount, totalCount: totalCount }));
                },
                enumerable: true,
                configurable: true
            },
            /** Introduction text shown when the processing accordion is expanded. */
            processingIntro: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.processingIntro?c=3v2yej4saba2e", defaultMessage: "Videos below will be available for reframe once processing completes." }));
                },
                enumerable: true,
                configurable: true
            },
            /** Per-row status shown next to a processing asset. */
            processingRowStatus: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.processingRowStatus?c=zoot8ho8pzld", defaultMessage: "Processing\u2026" }));
                },
                enumerable: true,
                configurable: true
            },
            /** Aria label for the small spinner indicating an asset is still processing. */
            processingSpinnerLabel: {
                get() {
                    return __intl__.withIntl(this[__intl__.BOUND_INTL] ?? __intl__.getIntl(), () => __intl__.intl().formatMessage({ id: "FireflyReframeDialog.processingSpinnerLabel?c=2emdeijebpb6f", defaultMessage: "Processing" }));
                },
                enumerable: true,
                configurable: true
            }
        });
    }
} as Text & {
    DEFINE(messages: Text, deps?: Deps): void;
};
